import type { track as TrackFn } from "pro-features/components/WithTrack/src/context";
import type { EnrichmentType } from "app/pages/sales-intelligence/modules/enrichment/types/types";
import createTrackingEventName from "app/pages/sales-intelligence/utils/testing/createTrackingEventName";

const createCsvEnrichmentTrackingService = (track: TrackFn) => {
    const getCategory = (type: EnrichmentType) => (type === "company" ? "csv enrichment" : "contact csv enrichment");

    return {
        trackTypeDDToggle: () => {
            track("csv enrichment", "open menu", "csv enrichment menu");
        },
        trackTypeDDClick: (type: EnrichmentType) => {
            track("csv enrichment", "select menu option", `upload csv/${type === "company" ? "Company" : "Contacts"}`);
        },
        trackApproveEnrichment: (totalRows: number, columnsAmount: number) => {
            track("csv enrichment", "enrich file", `approve enrichment/${totalRows}/${columnsAmount}`);
        },
        trackSelectHeaderMap: (eventNameProp: string, id: string, type: EnrichmentType) => {
            const eventName = createTrackingEventName(eventNameProp, id);
            const category = getCategory(type);
            track(category, "select column", eventName);
        },
        trackFileParseError: (error: string | undefined, type: EnrichmentType) => {
            const category = getCategory(type);
            track(category, "upload failed", `file not legal/${error}`);
        },
        trackCancelUpload: (type: EnrichmentType) => {
            const eventAction = type === "company" ? "cancel enrichment" : "cancel upload";
            track("csv enrichment", eventAction, "close popup");
        },
        trackFileUpload: (type: EnrichmentType) => {
            const category = getCategory(type);
            track(category, "Button", "upload csv");
        },
        trackFileDrop: (type: EnrichmentType) => {
            const category = getCategory(type);
            track(category, "Drag and drop", "upload csv");
        },
        trackApplyFields: (label: string, amount: number, type: EnrichmentType) => {
            const category = getCategory(type);
            track(category, "apply changes", `select values/${label}/${amount}`);
        },
        trackOpenCategory: (label: string, amount: number, type: EnrichmentType) => {
            const category = getCategory(type);
            track(category, "open category", `${label}/${amount}`);
        },
        trackSelectField: (id: string, label: string, amount: number, type: EnrichmentType) => {
            const category = getCategory(type);
            track(category, "add field", `${id}/${label}/${amount}`);
        },
        trackDeselectField: (id: string, label: string, amount: number, type: EnrichmentType) => {
            const category = getCategory(type);
            track(category, "remove field", `${id}/${label}/${amount}`);
        },
        trackSearchInCategory: (label: string, term: string, type: EnrichmentType) => {
            const category = getCategory(type);
            track(category, "search in category", `${label}/${term}`);
        },
        trackFileDownload: (total: number, enriched: number) => {
            track("enrichment archive", "download results", `enrichment results/${total}/${enriched}`);
        },
        company: {
            trackFileParse: (totalRows: number) => {
                track("csv enrichment", "parsed successfully", `file parsed/${totalRows}`);
            },
            trackCreateStaticList: (domainCount: number) => {
                track("csv enrichment", "save websites", `create static list/${domainCount}`);
            },
            discardStaticList: (domainCount: number) => {
                track("csv enrichment", "don`t monitor", String(domainCount));
            },
            trackSelectCountry: (country: string) => {
                track("csv enrichment", "select country", `visits from/${country}`);
            },
            trackCancelEnrichment: () => {
                track("csv enrichment", "cancel enrichment", "cancel upload");
            },
            trackCancelEnrichmentClosePopup: () => {
                track("csv enrichment", "cancel enrichment", "close popup");
            },
            trackApproveEnrichment: (totalRows: number, columnsAmount: number) => {
                track("csv enrichment", "enrich file", `approve enrichment/${totalRows}/${columnsAmount}`);
            },
            trackSuccessEnrichment: (totalRows: number, columnsAmount: number) => {
                track("csv enrichment", "enrichment status", `success/${totalRows}/${columnsAmount}`);
            },
            trackFailureEnrichment: (totalRows: number, columnsAmount: number) => {
                track("csv enrichment", "enrichment status", `failure/${totalRows}/${columnsAmount}`);
            },
        },
        contacts: {
            trackCancelEnrichment: () => {
                track("contact csv enrichment", "cancel enrichment", "close popup");
            },
            trackFileParse: (totalRows: number) => {
                track("contact csv enrichment", "parsed success", `file uploaded/${totalRows}`);
            },
            trackModalScroll: () => {
                track("contact csv enrichment", "scroll", "");
            },
            trackDownloadTemplate: () => {
                track("contact csv enrichment", "download template", "");
            },
            trackApproveEnrichment: (totalRows: number, columnsAmount: number) => {
                track("contact csv enrichment", "enrich file", `approve enrichment/${totalRows}/${columnsAmount}`);
            },
            trackSuccessEnrichment: (totalRows: number, enriched: number) => {
                track("contact csv enrichment", "enrichment status", `success/${totalRows}/${enriched}`);
            },
            trackFailureEnrichment: (totalRows: number) => {
                track("contact csv enrichment", "enrichment status", `failure/${totalRows}`);
            },
        },
    };
};

export default createCsvEnrichmentTrackingService;
